import { apiClient } from '../client';

/**
 * Client for `/api/cs/tickets` (Phase 16 / ADR-026). Stays in lockstep
 * with `server/src/routes/cs/tickets.routes.ts` and
 * `server/src/modules/cs/tickets/queue.ts` — mirror, don't invent.
 */

// ── Shared CS vocabulary ────────────────────────────────────────────────

export const CS_TICKET_STATUSES = ['new_ticket', 'open', 'pending', 'resolved', 'closed'] as const;
export const CS_TICKET_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;
export const CS_TIERS = ['tier1_auto', 'tier2_semi', 'tier3_human'] as const;

export type CsTicketStatus = (typeof CS_TICKET_STATUSES)[number];
export type CsTicketPriority = (typeof CS_TICKET_PRIORITIES)[number];
export type CsTier = (typeof CS_TIERS)[number];
export type CsSenderType = 'customer' | 'agent' | 'system';

/** Sentinel accepted by the queue's assignee filter (queue.ts). */
export const UNASSIGNED_FILTER = 'unassigned';

export interface CsMembershipRef {
  id: string;
  user: { firstName: string; lastName: string; email?: string };
}

// ── Queue ───────────────────────────────────────────────────────────────

/** Scalar columns of a CsTicket row (dates serialized as ISO strings). */
export interface CsTicketCore {
  id: string;
  organizationId: string;
  chatwootConversationId: number;
  chatwootInboxId: number | null;
  subject: string;
  status: CsTicketStatus;
  priority: CsTicketPriority;
  tier: CsTier;
  tags: string[];
  brand: string | null;
  channelType: string | null;
  csCustomerId: string;
  customerOrgId: string | null;
  groupId: string | null;
  groupEnteredAt: string | null;
  assignedMembershipId: string | null;
  firstAgentReplyAt: string | null;
  slaNudgeAt: string | null;
  slaEscalatedAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CsQueueTicket extends CsTicketCore {
  csCustomer: { id: string; email: string | null; firstName: string | null; lastName: string | null };
  customerOrg: { id: string; name: string; slug: string } | null;
  group: { id: string; name: string; slug: string } | null;
  assignedMembership: CsMembershipRef | null;
}

export interface TicketListFilters {
  status?: CsTicketStatus[];
  priority?: CsTicketPriority[];
  /** A membership id, or the literal 'unassigned'. */
  assigneeMembershipId?: string;
  groupId?: string;
  /** ANY-of match against the ticket's tag array. */
  tags?: string[];
  /** Brand filter — KwcOrgLink id. */
  kwcOrgLinkId?: string;
  tier?: CsTier;
  /** Case-insensitive contains over subject OR customer email. */
  q?: string;
}

export interface TicketListPage {
  rows: CsQueueTicket[];
  total: number;
  page: number;
  pageSize: number;
}

/** Filters → query string, matching the router's CSV conventions. */
export function serializeTicketFilters(filters: TicketListFilters, page = 1): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.status && filters.status.length > 0) params.set('status', filters.status.join(','));
  if (filters.priority && filters.priority.length > 0) params.set('priority', filters.priority.join(','));
  if (filters.assigneeMembershipId) params.set('assigneeMembershipId', filters.assigneeMembershipId);
  if (filters.groupId) params.set('groupId', filters.groupId);
  if (filters.tags && filters.tags.length > 0) params.set('tags', filters.tags.join(','));
  if (filters.kwcOrgLinkId) params.set('kwcOrgLinkId', filters.kwcOrgLinkId);
  if (filters.tier) params.set('tier', filters.tier);
  if (filters.q) params.set('q', filters.q);
  params.set('page', String(page));
  return params;
}

export async function listTickets(filters: TicketListFilters, page = 1): Promise<TicketListPage> {
  const params = serializeTicketFilters(filters, page);
  const res = await apiClient.get<{ data: TicketListPage }>(`/api/cs/tickets?${params.toString()}`);
  return res.data.data;
}

// ── Detail ──────────────────────────────────────────────────────────────

export interface CsCustomer {
  id: string;
  organizationId: string;
  chatwootContactId: number | null;
  email: string | null;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  kwcCustomerId: string | null;
  customerOrgId: string | null;
  isPractitioner: boolean;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CsTicketMessage {
  id: string;
  organizationId: string;
  csTicketId: string;
  chatwootMessageId: number | null;
  senderType: CsSenderType;
  senderMembershipId: string | null;
  senderMembership?: { id: string; user: { firstName: string; lastName: string } } | null;
  content: string;
  isPrivate: boolean;
  isAutoReply: boolean;
  createdAt: string;
}

export interface CsTriageDecision {
  id: string;
  organizationId: string;
  csTicketId: string;
  assignedTier: CsTier;
  confidence: number;
  reasoning: string;
  suggestedTags: string[];
  suggestedResponse: string | null;
  tierDowngraded: boolean;
  wasOverridden: boolean;
  overriddenTier: CsTier | null;
  overrideReason: string | null;
  reviewedByMembershipId: string | null;
  model: string;
  promptTokens: number | null;
  completionTokens: number | null;
  latencyMs: number | null;
  createdAt: string;
}

export interface CsAssignmentEvent {
  id: string;
  organizationId: string;
  csTicketId: string;
  fromMembershipId: string | null;
  toMembershipId: string | null;
  fromGroupId: string | null;
  toGroupId: string | null;
  actorMembershipId: string | null;
  actorMembership?: { id: string; user: { firstName: string; lastName: string } } | null;
  source: 'rule' | 'fallback' | 'manual' | 'chatwoot_sync' | 'triage';
  createdAt: string;
}

export interface CsEscalation {
  id: string;
  organizationId: string;
  csTicketId: string;
  fromGroupId: string | null;
  toGroupId: string;
  reason: string;
  notes: string | null;
  createdByMembershipId: string | null;
  createdAt: string;
}

export interface CsTicketDetail extends CsTicketCore {
  csCustomer: CsCustomer;
  customerOrg: { id: string; name: string; slug: string; tags: string[] } | null;
  group: { id: string; name: string; slug: string } | null;
  assignedMembership: CsMembershipRef | null;
  triageDecision: CsTriageDecision | null;
  messages: CsTicketMessage[];
  escalations: CsEscalation[];
  assignmentEvents: CsAssignmentEvent[];
}

export async function getTicket(ticketId: string): Promise<CsTicketDetail> {
  const res = await apiClient.get<{ data: CsTicketDetail }>(
    `/api/cs/tickets/${encodeURIComponent(ticketId)}`,
  );
  return res.data.data;
}

// ── Mutations ───────────────────────────────────────────────────────────

export async function replyToTicket(ticketId: string, content: string): Promise<CsTicketMessage> {
  const res = await apiClient.post<{ data: CsTicketMessage }>(
    `/api/cs/tickets/${encodeURIComponent(ticketId)}/reply`,
    { content },
  );
  return res.data.data;
}

export async function addTicketNote(ticketId: string, content: string): Promise<CsTicketMessage> {
  const res = await apiClient.post<{ data: CsTicketMessage }>(
    `/api/cs/tickets/${encodeURIComponent(ticketId)}/note`,
    { content },
  );
  return res.data.data;
}

export interface AssignTicketInput {
  /** Membership id, or null to unassign. Omit to leave untouched. */
  assigneeMembershipId?: string | null;
  /** Group id, or null to clear. Omit to leave untouched. */
  groupId?: string | null;
}

export async function assignTicket(ticketId: string, input: AssignTicketInput): Promise<CsTicketCore> {
  const res = await apiClient.post<{ data: CsTicketCore }>(
    `/api/cs/tickets/${encodeURIComponent(ticketId)}/assign`,
    input,
  );
  return res.data.data;
}

export async function setTicketStatus(ticketId: string, status: CsTicketStatus): Promise<CsTicketCore> {
  const res = await apiClient.post<{ data: CsTicketCore }>(
    `/api/cs/tickets/${encodeURIComponent(ticketId)}/status`,
    { status },
  );
  return res.data.data;
}

export async function setTicketPriority(ticketId: string, priority: CsTicketPriority): Promise<CsTicketCore> {
  const res = await apiClient.post<{ data: CsTicketCore }>(
    `/api/cs/tickets/${encodeURIComponent(ticketId)}/priority`,
    { priority },
  );
  return res.data.data;
}

/** Full-replace semantics — send the complete desired tag list. */
export async function setTicketTags(ticketId: string, tags: string[]): Promise<CsTicketCore> {
  const res = await apiClient.post<{ data: CsTicketCore }>(
    `/api/cs/tickets/${encodeURIComponent(ticketId)}/tags`,
    { tags },
  );
  return res.data.data;
}

export interface MacroApplication {
  /** Steps that executed, in order: reply, note, add_tags, assign, set_status. */
  applied: string[];
}

export async function applyMacroToTicket(ticketId: string, macroId: string): Promise<MacroApplication> {
  const res = await apiClient.post<{ data: MacroApplication }>(
    `/api/cs/tickets/${encodeURIComponent(ticketId)}/macro`,
    { macroId },
  );
  return res.data.data;
}

export interface EscalateTicketInput {
  toGroupId: string;
  reason: string;
  notes?: string;
}

export async function escalateTicket(ticketId: string, input: EscalateTicketInput): Promise<CsEscalation> {
  const res = await apiClient.post<{ data: CsEscalation }>(
    `/api/cs/tickets/${encodeURIComponent(ticketId)}/escalate`,
    input,
  );
  return res.data.data;
}

// ── Bulk ────────────────────────────────────────────────────────────────

export type BulkTicketAction =
  | { type: 'assign'; assigneeMembershipId?: string | null; groupId?: string | null }
  | { type: 'set_status'; status: CsTicketStatus }
  | { type: 'set_priority'; priority: CsTicketPriority }
  | { type: 'add_tags'; tags: string[] };

export interface BulkTicketResult {
  ticketId: string;
  ok: boolean;
  error?: { code: string; message: string };
}

export async function bulkTicketAction(
  ticketIds: string[],
  action: BulkTicketAction,
): Promise<BulkTicketResult[]> {
  const res = await apiClient.post<{ data: { results: BulkTicketResult[] } }>('/api/cs/tickets/bulk', {
    ticketIds,
    action,
  });
  return res.data.data.results;
}
